import type { MentionItem } from 'react-mentions';
import type { CommentIdComponents, PostCount, RootComment } from '@gcv/shared';
import { CommentType, CommentTypeUtil } from '@gcv/shared';
import { injectable } from 'inversify';
import { action, makeAutoObservable, runInAction } from 'mobx';

import { CommentsApi } from 'api';

export interface CommentDraft {
  commentVal: string;
  files: File[];
  reply: RootComment | null;
  mentionedUsers: MentionItem[];
  isExternalComment: boolean;
  createTask: boolean;
}

@injectable()
export class CommentStore {
  activeTab: 'comments' | 'activity' | 'archived' = 'comments';
  commentsOpen = false;
  currentPostId: string | null = null;
  idComponents: CommentIdComponents = {} as CommentIdComponents;
  fetchingCount = false;
  loading = false;
  countsMap: PostCount = {};
  postTitle = '';
  type: CommentType = CommentType.ACCOUNT;
  currentlyFetchingCount = false;
  drafts: Map<string, CommentDraft> = new Map();

  commentsApi = new CommentsApi();

  constructor() {
    makeAutoObservable(this);
  }

  private hasDraftContent = (draft: CommentDraft): boolean => {
    return (
      draft.commentVal.trim().length > 0 ||
      draft.files.length > 0 ||
      draft.reply !== null ||
      draft.mentionedUsers.length > 0
    );
  };

  saveDraft = action((postId: string, draft: CommentDraft) => {
    if (this.hasDraftContent(draft)) {
      this.drafts.set(postId, draft);
    } else {
      this.drafts.delete(postId);
    }
  });

  getDraft = (postId: string): CommentDraft | undefined => {
    return this.drafts.get(postId);
  };

  clearDraft = action((postId: string) => {
    this.drafts.delete(postId);
  });

  generatePostId = (post: { type: CommentType; idComponents: CommentIdComponents }): string => {
    return (
      CommentTypeUtil.getCommentPostIdv2(
        post.type,
        post.idComponents.fiId,
        post.idComponents.crbId,
        post.idComponents
      ) ?? ''
    );
  };

  setCurrentPost = action(
    (post: { type: CommentType; title: string; idComponents: CommentIdComponents } | null) => {
      if (post) {
        this.type = post.type;
        this.postTitle = post.title;
        this.idComponents = post.idComponents;
        this.currentPostId = this.generatePostId(post);
      } else {
        this.currentPostId = null;
      }
    }
  );

  openComments = action((isOpen: boolean, tab?: 'comments' | 'activity' | 'archived') => {
    // we only need a loading spinner if opening a different comment and the comment drawer is already open
    if (this.commentsOpen) {
      this.loading = true;
    }

    if (tab) {
      this.activeTab = tab;
    }

    this.commentsOpen = isOpen;

    // reset to comments tab on close for openComments calls that do not pass in a tab
    if (!isOpen) {
      this.activeTab = 'comments';
    }

    // artificial delay per request from product team
    setTimeout(() => {
      runInAction(() => {
        this.loading = false;
      });
    }, 500);
  });

  shouldFetchCount = action((fetch: boolean) => {
    this.fetchingCount = fetch;
  });

  fetchCount = action(async (posts: { type: CommentType; idComponents: CommentIdComponents }[]) => {
    const postIds = posts.map((post) => this.generatePostId(post)).filter((p) => p);

    if (postIds.length === 0) {
      // Otherwise, we get a 400 response: "Error: No Post Ids given".
      return;
    }

    try {
      if (this.currentlyFetchingCount && postIds.length > 0 && postIds[0] === this.currentPostId) return;
      this.currentlyFetchingCount = true;
      const counts = await this.commentsApi.getCommentCount(postIds);
      this.currentlyFetchingCount = false;
      runInAction(() => {
        Object.keys(counts).forEach((key) => {
          this.countsMap[key] = counts[key];
        });

        this.fetchingCount = false;
      });
    } catch (e) {
      console.log(e);
    }
  });

  activateTab = action((tab: 'comments' | 'activity' | 'archived') => {
    this.activeTab = tab;
  });

  clearStore = action(() => {
    this.countsMap = {};
    this.drafts.clear();
  });
}

let commentStore: CommentStore | undefined;

export function getCommentStore(): CommentStore {
  if (!commentStore) {
    commentStore = new CommentStore();
  }

  return commentStore;
}
